/**
 * Tenant Resolver
 * ───────────────
 * Determines the active tenant from the runtime environment (subdomain,
 * query param, or stored preference).
 *
 * Priority order
 * ──────────────
 *   1. Subdomain (production):
 *        acme.clearequal.com → 'acme' → resolves to tenant 'global-bank-es'
 *
 *   2. Query parameter (preview deployments, local dev):
 *        clearequal.com?tenant=acme
 *        localhost:4028?tenant=acme
 *
 *   3. localStorage (sticky session preference):
 *        previously selected tenant from the in-app switcher
 *
 *   4. Default fallback: DEFAULT_TENANT_ID
 *
 * Reserved subdomains
 * ───────────────────
 * The following subdomains are NOT customer tenants and fall through to
 * the marketing site or app shell with no tenant context:
 *
 *   www, app, api, docs, status, uat, staging, beta, admin
 */

import { SUBDOMAIN_TO_TENANT } from '../data/tenantBranding';
import { TENANTS, DEFAULT_TENANT_ID } from '../data/tenants';

const RESERVED_SUBDOMAINS = new Set([
  'www', 'app', 'api', 'docs', 'status', 'uat',
  'staging', 'beta', 'admin', 'demo', 'preview',
]);

/**
 * Extract the leftmost subdomain from a hostname.
 *  acme.clearequal.com       → 'acme'
 *  clearequal.com            → null  (apex)
 *  www.clearequal.com        → 'www' (reserved)
 *  localhost                 → null
 *  acme.uat.clearequal.com   → 'acme' (skip env-tier label)
 *  clearequal-uat.vercel.app → null  (preview deploys: use ?tenant=)
 */
export const extractSubdomain = (hostname) => {
  if (!hostname || hostname === 'localhost') return null;

  // Vercel preview deploys (e.g., clearequal-git-main.vercel.app)
  if (hostname.endsWith('.vercel.app')) return null;

  // IP address (rare but possible during local network testing)
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null;

  const parts = hostname.split('.');
  if (parts.length < 3) return null; // apex domain like clearequal.com

  return parts[0].toLowerCase();
};

/**
 * Get the tenant ID from URL query parameter.
 */
const getTenantFromQuery = () => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const slug = params.get('tenant');
  if (!slug) return null;

  // Accept either the subdomain slug ('acme') or the full tenant ID
  if (SUBDOMAIN_TO_TENANT[slug]) return SUBDOMAIN_TO_TENANT[slug];
  if (TENANTS[slug]) return slug;
  return null;
};

/**
 * Get the tenant ID from localStorage (in-app switcher selection).
 */
const STORAGE_KEY = 'clearequal.activeTenant';
const getTenantFromStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && TENANTS[stored] ? stored : null;
  } catch {
    return null;
  }
};

/**
 * Resolve the active tenant ID. Returns the tenant ID string (e.g.
 * 'global-bank-es') and the resolution source for telemetry/debugging.
 */
export const resolveTenant = () => {
  // 1. Subdomain (highest priority, locks tenant for white-labelled URL)
  if (typeof window !== 'undefined') {
    const sub = extractSubdomain(window.location.hostname);
    if (sub && !RESERVED_SUBDOMAINS.has(sub) && SUBDOMAIN_TO_TENANT[sub]) {
      return {
        tenantId: SUBDOMAIN_TO_TENANT[sub],
        source: 'subdomain',
        subdomain: sub,
        locked: true, // user cannot switch via in-app picker on a tenant subdomain
      };
    }
  }

  // 2. Query parameter
  const fromQuery = getTenantFromQuery();
  if (fromQuery) {
    return { tenantId: fromQuery, source: 'query', locked: false };
  }

  // 3. Stored preference
  const fromStorage = getTenantFromStorage();
  if (fromStorage) {
    return { tenantId: fromStorage, source: 'storage', locked: false };
  }

  // 4. Default
  return { tenantId: DEFAULT_TENANT_ID, source: 'default', locked: false };
};

/**
 * Returns true if we're running on a customer-branded tenant subdomain
 * where the tenant switcher should be hidden.
 */
export const isTenantLocked = () => {
  const { locked } = resolveTenant();
  return locked;
};

/**
 * Returns the current host environment label for the BETA banner.
 *   localhost / *.local         → 'local'
 *   *.vercel.app                → 'preview'
 *   uat.clearequal.com          → 'uat'
 *   <tenant>.clearequal.com     → 'production'
 *   clearequal.com              → 'production'
 */
export const getEnvironment = () => {
  if (typeof window === 'undefined') return 'unknown';
  const h = window.location.hostname;

  if (h === 'localhost' || h.endsWith('.local')) return 'local';
  if (h.endsWith('.vercel.app')) return 'preview';
  if (h.startsWith('uat.') || h.startsWith('staging.') || h.startsWith('beta.')) return 'uat';
  return 'production';
};
